import { useCallback, useEffect, useState } from "react";
import {
  MdSearch,
  MdRefresh,
  MdMailOutline,
  MdAssignment,
  MdLink,
  MdContentCopy,
} from "react-icons/md";
import { useNavigate, useSearchParams } from "react-router-dom";
import Card from "components/card";
import {
  reskillingService,
  ReskillingEvaluacion,
  ReskillingResumen,
} from "services/reskillingService";

/** Configuración visual de cada estado. */
const ESTADOS: Record<
  string,
  { etiqueta: string; clase: string }
> = {
  completed: {
    etiqueta: "Completada",
    clase: "bg-green-100 text-green-700",
  },
  in_progress: {
    etiqueta: "En curso",
    clase: "bg-blue-100 text-blue-700",
  },
  pending: {
    etiqueta: "Sin empezar",
    clase: "bg-gray-100 text-gray-600",
  },
};

const FILTROS = [
  { valor: "", etiqueta: "Todas" },
  { valor: "in_progress", etiqueta: "En curso" },
  { valor: "completed", etiqueta: "Completadas" },
  { valor: "pending", etiqueta: "Sin empezar" },
];

const Evaluaciones = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Desde la ficha de un cliente se llega con ?cliente=<id>.
  const clienteId = searchParams.get("cliente") ?? undefined;
  const [evaluaciones, setEvaluaciones] = useState<ReskillingEvaluacion[]>([]);
  const [resumen, setResumen] = useState<ReskillingResumen | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState("");
  const [filtro, setFiltro] = useState("");
  const [reenviando, setReenviando] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [pagina, setPagina] = useState(1);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);

    try {
      const { evaluaciones, resumen, meta } =
        await reskillingService.listarEvaluaciones({
          search: busqueda || undefined,
          status: filtro || undefined,
          cliente_id: clienteId,
          page: pagina,
        });
      setEvaluaciones(evaluaciones);
      setResumen(resumen);
      setMeta(meta);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "No se pudieron cargar las evaluaciones."
      );
    } finally {
      setCargando(false);
    }
  }, [busqueda, filtro, clienteId, pagina]);

  useEffect(() => {
    // Espera a que el usuario deje de tipear antes de consultar.
    const id = setTimeout(cargar, busqueda ? 400 : 0);
    return () => clearTimeout(id);
  }, [cargar, busqueda]);

  // Al cambiar los filtros, la página actual deja de tener sentido.
  useEffect(() => {
    setPagina(1);
  }, [busqueda, filtro, clienteId]);

  const copiarLink = (url: string) => {
    navigator.clipboard.writeText(url).catch(() => {});
    setAviso("Link copiado al portapapeles.");
    setTimeout(() => setAviso(null), 3000);
  };

  const recordar = async (sessionId: string) => {
    setReenviando(sessionId);
    setAviso(null);

    try {
      await reskillingService.resendLink(sessionId);
      setAviso("Recordatorio enviado.");
    } catch {
      setAviso("No se pudo enviar el recordatorio.");
    } finally {
      setReenviando(null);
      setTimeout(() => setAviso(null), 4000);
    }
  };

  return (
    <div className="mt-5">
      {resumen && (
        <div className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Tarjeta titulo="Total" valor={resumen.total} />
          <Tarjeta
            titulo="Completadas"
            valor={resumen.completadas}
            color="text-green-600"
          />
          <Tarjeta
            titulo="En curso"
            valor={resumen.en_curso}
            color="text-blue-600"
          />
          <Tarjeta
            titulo="Sin empezar"
            valor={resumen.sin_empezar}
            color="text-gray-500"
          />
        </div>
      )}

      <Card extra="w-full p-4">
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <h2 className="text-xl font-bold text-navy-700 dark:text-white">
            Evaluaciones de habilidades
          </h2>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar por nombre o email"
                className="rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none dark:bg-navy-800 dark:text-white"
              />
            </div>

            <select
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:bg-navy-800 dark:text-white"
            >
              {FILTROS.map((f) => (
                <option key={f.valor} value={f.valor}>
                  {f.etiqueta}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={cargar}
              className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 dark:text-white"
            >
              <MdRefresh /> Actualizar
            </button>
          </div>
        </div>

        {aviso && (
          <div className="mb-3 rounded-md bg-blue-50 p-2 text-sm text-blue-700">
            {aviso}
          </div>
        )}

        {error && (
          <div className="mb-3 rounded-md bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {cargando ? (
          <div className="py-10 text-center text-gray-500">Cargando…</div>
        ) : evaluaciones.length === 0 ? (
          <div className="py-10 text-center text-gray-500">
            Todavía no hay evaluaciones.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500 dark:border-white/10">
                  <th className="pb-2 pr-4">Persona</th>
                  <th className="pb-2 pr-4">Cliente</th>
                  <th className="pb-2 pr-4">Estado</th>
                  <th className="pb-2 pr-4">Inicio</th>
                  <th className="pb-2 pr-4">Última actividad</th>
                  <th className="pb-2">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {evaluaciones.map((e) => {
                  const estado = ESTADOS[e.status] ?? {
                    etiqueta: e.status,
                    clase: "bg-gray-100 text-gray-600",
                  };

                  return (
                    <tr
                      key={e.session_id}
                      className="border-b border-gray-100 text-sm dark:border-white/5"
                    >
                      <td className="py-3 pr-4">
                        <p className="font-medium text-navy-700 dark:text-white">
                          {e.nombre || "—"}
                        </p>
                        <p className="text-xs text-gray-500">{e.email}</p>
                      </td>

                      <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                        {e.cliente || "—"}
                      </td>

                      <td className="py-3 pr-4">
                        <span
                          className={`inline-block rounded-full px-2.5 py-1 text-xs font-medium ${estado.clase}`}
                        >
                          {estado.etiqueta}
                        </span>
                        {e.esta_expirada && !e.tiene_resultados && (
                          <span className="ml-1 inline-block rounded-full bg-orange-100 px-2 py-1 text-xs text-orange-700">
                            Expirada
                          </span>
                        )}
                      </td>

                      <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                        {formatearFecha(e.creada_el)}
                      </td>

                      <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                        {e.status === "completed"
                          ? formatearFecha(e.completada_el)
                          : describirEspera(e.dias_sin_actividad)}
                      </td>

                      <td className="py-3">
                        {e.status === "completed" ? (
                          <div className="flex flex-col gap-1">
                            <button
                              type="button"
                              onClick={() =>
                                navigate(`/admin/evaluaciones/${e.session_id}`)
                              }
                              className="flex items-center gap-1 text-xs text-brand-500 hover:underline"
                            >
                              <MdAssignment /> Ver informe
                            </button>

                            {/* El mismo informe que recibe la persona evaluada. */}
                            {e.url_informe_compartido && (
                              <div className="flex items-center gap-2">
                                <a
                                  href={e.url_informe_compartido}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-brand-500 hover:underline"
                                >
                                  <MdLink /> El del cliente
                                </a>
                                <button
                                  type="button"
                                  onClick={() => copiarLink(e.url_informe_compartido!)}
                                  className="text-xs text-gray-400 hover:text-brand-500"
                                  title="Copiar link"
                                >
                                  <MdContentCopy />
                                </button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => recordar(e.session_id)}
                            disabled={reenviando === e.session_id}
                            className="flex items-center gap-1 text-xs text-brand-500 hover:underline disabled:opacity-50"
                          >
                            <MdMailOutline />
                            {reenviando === e.session_id
                              ? "Enviando…"
                              : "Recordar"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {meta.last_page > 1 && (
          <div className="mt-4 flex items-center justify-between border-t pt-3 text-sm">
            <span className="text-gray-500">
              Página {meta.current_page} de {meta.last_page} · {meta.total}{" "}
              evaluaciones
            </span>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPagina((p) => Math.max(1, p - 1))}
                disabled={meta.current_page <= 1}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                Anterior
              </button>
              <button
                type="button"
                onClick={() => setPagina((p) => p + 1)}
                disabled={meta.current_page >= meta.last_page}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

function Tarjeta({
  titulo,
  valor,
  color = "text-navy-700 dark:text-white",
}: {
  titulo: string;
  valor: number;
  color?: string;
}) {
  return (
    <Card extra="p-4">
      <p className="text-sm text-gray-500">{titulo}</p>
      <p className={`text-2xl font-bold ${color}`}>{valor}</p>
    </Card>
  );
}

function formatearFecha(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function describirEspera(dias: number | null): string {
  if (dias === null) return "—";
  if (dias === 0) return "Hoy";
  if (dias === 1) return "Hace 1 día";
  return `Hace ${dias} días`;
}

export default Evaluaciones;
